import { GAME_MODE, SongPreview } from '~/interfaces';
import { hitRateOf } from '~/modules/game-engine/game-state/helpers/calculate-note-stats';
import { gradeOf, ProfilePlay, ProfileStats, summarisePlays } from '~/modules/user-profiles/profile-stats';

/** Song metadata a few achievements need, looked up by the key `SongStats` files plays under. */
export type SongLookup = Record<string, Pick<SongPreview, 'genre' | 'language'>>;

export interface AchievementDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  /** How many of whatever it counts are needed. */
  target: number;
  group: 'Milestones' | 'Variety' | 'Score' | 'Grades' | 'Together' | 'Streaks' | 'Around the world' | 'Singing';
}

export interface Achievement extends AchievementDefinition {
  /** Capped at `target`, so a progress bar never overshoots. */
  progress: number;
  unlocked: boolean;
}

interface Progress extends AchievementDefinition {
  measure: (context: Context) => number;
}

interface Context {
  stats: ProfileStats;
  songs: SongLookup;
}

/** Two plays belong to the same sitting if they are less than this apart. */
const SESSION_GAP_MS = 30 * 60 * 1000;

const distinctSongs = ({ stats }: Context) => stats.songCount;
const playCount = ({ stats }: Context) => stats.plays.length;

const bestScoreAtLeast =
  (threshold: number) =>
  ({ stats }: Context) =>
    stats.highestScore >= threshold ? 1 : 0;

/** Distinct songs whose best score reached the given grade or better. */
const songsAtGrade =
  (grades: string[]) =>
  ({ stats }: Context) =>
    Object.values(stats.bestPlayPerSong).filter((play) => grades.includes(gradeOf(play))).length;

const playsWithOthers = ({ stats }: Context) =>
  stats.plays.filter((play) => play.setup.mode === GAME_MODE.CO_OP || play.others.length > 0).length;

/** The longest run of plays with no more than `SESSION_GAP_MS` between them. */
const longestSession = ({ stats }: Context) => {
  const times = stats.plays.map((play) => new Date(play.date).getTime()).sort((a, b) => a - b);
  let longest = 0;
  let run = 0;

  for (let i = 0; i < times.length; i++) {
    run = i > 0 && times[i] - times[i - 1] < SESSION_GAP_MS ? run + 1 : 1;
    longest = Math.max(longest, run);
  }

  return longest;
};

const songsMatching =
  (matches: (song: Pick<SongPreview, 'genre' | 'language'>) => boolean) =>
  ({ stats, songs }: Context) => {
    const sung = new Set<string>();

    for (const play of stats.plays) {
      const song = songs[play.songKey];
      if (song && matches(song)) sung.add(play.songKey);
    }

    return sung.size;
  };

/** Distinct values of a metadata field across everything the profile has sung. */
const distinctMetadata =
  (pick: (song: Pick<SongPreview, 'genre' | 'language'>) => string[] | undefined) =>
  ({ stats, songs }: Context) => {
    const values = new Set<string>();

    for (const play of stats.plays) {
      for (const value of pick(songs[play.songKey] ?? {}) ?? []) {
        if (value) values.add(value.toLowerCase());
      }
    }

    return values.size;
  };

/**
 * Only plays whose notes were measured. Plays recorded before that carry no `notes`, so these
 * count up from the point it started being recorded rather than claiming the earlier ones missed.
 */
const measured = (stats: ProfileStats) => stats.plays.filter((play) => play.notes !== undefined);

// Counted on the share of notes that scored, not on the weighted accuracy the results screen
// shows: these promise a number of notes hit, and weighing the near ones in would move every
// threshold without a word of their wording changing.
const playsAtHitRate =
  (share: number) =>
  ({ stats }: Context) =>
    measured(stats).filter((play) => hitRateOf(play.notes!) >= share).length;

const bestCombo = ({ stats }: Context) =>
  measured(stats).reduce((best, play) => Math.max(best, play.notes!.maxCombo), 0);

const comboAtLeast = (target: number) => (context: Context) => (bestCombo(context) >= target ? 1 : 0);

const totalPerfectNotes = ({ stats }: Context) =>
  measured(stats).reduce((total, play) => total + play.notes!.perfect, 0);

/** Best improvement on a single song, as a share of the first score. */
const improvedBy =
  (share: number) =>
  ({ stats }: Context) => {
    const bySong = new Map<string, ProfilePlay[]>();
    for (const play of stats.plays) {
      bySong.set(play.songKey, [...(bySong.get(play.songKey) ?? []), play]);
    }

    for (const plays of bySong.values()) {
      if (plays.length < 2) continue;
      const inOrder = [...plays].sort((a, b) => a.date.localeCompare(b.date));
      const first = inOrder[0].score;
      const best = Math.max(...inOrder.slice(1).map((play) => play.score));
      // A first score of zero has no share to improve on, and every later score would divide by it.
      if (first > 0 && (best - first) / first >= share) return 1;
    }

    return 0;
  };

/**
 * Every achievement is measured from the plays the game already recorded, so they apply to a
 * profile's whole history the moment it is created - including songs sung before it existed.
 */
const ACHIEVEMENTS: Progress[] = [
  {
    id: 'first-song',
    name: 'First Song',
    description: 'Sing your first song',
    icon: '🎤',
    target: 1,
    group: 'Milestones',
    measure: playCount,
  },
  {
    id: 'songs-10',
    name: 'Dedicated',
    description: 'Sing 10 songs',
    icon: '🎵',
    target: 10,
    group: 'Milestones',
    measure: playCount,
  },
  {
    id: 'songs-25',
    name: 'Quarter Century',
    description: 'Sing 25 songs',
    icon: '🎶',
    target: 25,
    group: 'Milestones',
    measure: playCount,
  },
  {
    id: 'songs-50',
    name: 'Half Century',
    description: 'Sing 50 songs',
    icon: '🎼',
    target: 50,
    group: 'Milestones',
    measure: playCount,
  },
  {
    id: 'songs-100',
    name: 'Centurion',
    description: 'Sing 100 songs',
    icon: '🏅',
    target: 100,
    group: 'Milestones',
    measure: playCount,
  },
  {
    id: 'songs-500',
    name: 'Legendary',
    description: 'Sing 500 songs',
    icon: '👑',
    target: 500,
    group: 'Milestones',
    measure: playCount,
  },

  {
    id: 'different-10',
    name: 'Song Explorer',
    description: 'Sing 10 different songs',
    icon: '🗺️',
    target: 10,
    group: 'Variety',
    measure: distinctSongs,
  },
  {
    id: 'different-25',
    name: 'Song Collector',
    description: 'Sing 25 different songs',
    icon: '📚',
    target: 25,
    group: 'Variety',
    measure: distinctSongs,
  },
  {
    id: 'different-50',
    name: 'Song Master',
    description: 'Sing 50 different songs',
    icon: '🏆',
    target: 50,
    group: 'Variety',
    measure: distinctSongs,
  },

  {
    id: 'score-1m',
    name: 'Century',
    description: 'Score over 1,000,000 points',
    icon: '💯',
    target: 1,
    group: 'Score',
    measure: bestScoreAtLeast(1_000_000),
  },
  {
    id: 'score-2m',
    name: 'Two Million Club',
    description: 'Score over 2,000,000 points',
    icon: '💎',
    target: 1,
    group: 'Score',
    measure: bestScoreAtLeast(2_000_000),
  },
  {
    id: 'score-3m',
    name: 'Three Million Club',
    description: 'Score over 3,000,000 points',
    icon: '🌟',
    target: 1,
    group: 'Score',
    measure: bestScoreAtLeast(3_000_000),
  },
  {
    id: 'comeback',
    name: 'Comeback King',
    description: 'Beat your own score on a song by 30%',
    icon: '📈',
    target: 1,
    group: 'Score',
    measure: improvedBy(0.3),
  },

  {
    id: 'grade-s',
    name: 'S Rank',
    description: 'Get an S grade on any song',
    icon: '🌠',
    target: 1,
    group: 'Grades',
    measure: songsAtGrade(['S']),
  },
  {
    id: 'grade-s-5',
    name: 'S Collector',
    description: 'Get an S grade on 5 different songs',
    icon: '✨',
    target: 5,
    group: 'Grades',
    measure: songsAtGrade(['S']),
  },
  {
    id: 'grade-a-10',
    name: 'A-List',
    description: 'Get an A grade or better on 10 different songs',
    icon: '🅰️',
    target: 10,
    group: 'Grades',
    measure: songsAtGrade(['S', 'A']),
  },

  {
    id: 'together-5',
    name: 'Duo Master',
    description: 'Sing 5 songs with somebody else',
    icon: '👥',
    target: 5,
    group: 'Together',
    measure: playsWithOthers,
  },
  {
    id: 'together-20',
    name: 'Party Animal',
    description: 'Sing 20 songs with somebody else',
    icon: '🎉',
    target: 20,
    group: 'Together',
    measure: playsWithOthers,
  },

  {
    id: 'session-3',
    name: 'Hat Trick',
    description: 'Sing 3 songs in one sitting',
    icon: '🎩',
    target: 3,
    group: 'Streaks',
    measure: longestSession,
  },
  {
    id: 'session-5',
    name: 'On Fire',
    description: 'Sing 5 songs in one sitting',
    icon: '🔥',
    target: 5,
    group: 'Streaks',
    measure: longestSession,
  },
  {
    id: 'session-10',
    name: 'Marathon',
    description: 'Sing 10 songs in one sitting',
    icon: '🏃',
    target: 10,
    group: 'Streaks',
    measure: longestSession,
  },

  {
    id: 'accuracy-perfect',
    name: 'Perfect Performance',
    description: 'Hit every note in a song',
    icon: '⭐',
    target: 1,
    group: 'Singing',
    measure: playsAtHitRate(1),
  },
  {
    id: 'all-perfect',
    name: 'Pitch Perfect',
    description: 'Hit every note in a song perfectly',
    icon: '🎛️',
    target: 1,
    group: 'Singing',
    // Stricter than hitting every note: every one held cleanly enough to count as perfect.
    measure: ({ stats }: Context) =>
      measured(stats).filter((play) => play.notes!.total > 0 && play.notes!.perfect === play.notes!.total).length > 0
        ? 1
        : 0,
  },
  {
    id: 'accuracy-90-5',
    name: 'Sharpshooter',
    description: 'Hit 90% of the notes in 5 songs',
    icon: '🎯',
    target: 5,
    group: 'Singing',
    measure: playsAtHitRate(0.9),
  },
  {
    id: 'accuracy-95-3',
    name: 'Sniper',
    description: 'Hit 95% of the notes in 3 songs',
    icon: '🔫',
    target: 3,
    group: 'Singing',
    measure: playsAtHitRate(0.95),
  },
  {
    id: 'combo-50',
    name: 'Combo Starter',
    description: 'Hit 50 notes in a row',
    icon: '🔥',
    target: 1,
    group: 'Singing',
    measure: comboAtLeast(50),
  },
  {
    id: 'combo-100',
    name: 'Combo Master',
    description: 'Hit 100 notes in a row',
    icon: '💥',
    target: 1,
    group: 'Singing',
    measure: comboAtLeast(100),
  },
  {
    id: 'combo-200',
    name: 'Unstoppable',
    description: 'Hit 200 notes in a row',
    icon: '⚡',
    target: 1,
    group: 'Singing',
    measure: comboAtLeast(200),
  },
  {
    id: 'perfect-notes-100',
    name: 'Golden Voice',
    description: 'Hit 100 perfect notes',
    icon: '🥇',
    target: 100,
    group: 'Singing',
    measure: totalPerfectNotes,
  },
  {
    id: 'perfect-notes-500',
    name: 'Diamond Voice',
    description: 'Hit 500 perfect notes',
    icon: '💠',
    target: 500,
    group: 'Singing',
    measure: totalPerfectNotes,
  },
  {
    id: 'perfect-notes-1000',
    name: 'Crystal Voice',
    description: 'Hit 1000 perfect notes',
    icon: '🔮',
    target: 1000,
    group: 'Singing',
    measure: totalPerfectNotes,
  },
  {
    id: 'japanese-10',
    name: 'Anime Fan',
    description: 'Sing 10 Japanese songs',
    icon: '🇯🇵',
    target: 10,
    group: 'Around the world',
    measure: songsMatching((song) => (song.language ?? []).some((language) => language.toLowerCase() === 'japanese')),
  },
  {
    id: 'genres-5',
    name: 'Genre Explorer',
    description: 'Sing songs from 5 different genres',
    icon: '🌍',
    target: 5,
    group: 'Around the world',
    measure: distinctMetadata((song) => (song.genre ? [song.genre] : [])),
  },
  {
    id: 'genres-10',
    name: 'Genre Master',
    description: 'Sing songs from 10 different genres',
    icon: '🌐',
    target: 10,
    group: 'Around the world',
    measure: distinctMetadata((song) => (song.genre ? [song.genre] : [])),
  },
  {
    id: 'languages-3',
    name: 'Polyglot',
    description: 'Sing in 3 different languages',
    icon: '🗣️',
    target: 3,
    group: 'Around the world',
    measure: distinctMetadata((song) => song.language),
  },
  {
    id: 'languages-5',
    name: 'Linguist',
    description: 'Sing in 5 different languages',
    icon: '📖',
    target: 5,
    group: 'Around the world',
    measure: distinctMetadata((song) => song.language),
  },
];

/**
 * The same achievements bucketed by group, in the order `ACHIEVEMENTS` declares them - both the
 * groups themselves and the entries inside each one. Derived rather than listed, so a group can
 * never fall off a screen by being forgotten in a second place.
 */
export const groupAchievements = (achievements: Achievement[]) => {
  const groups = new Map<AchievementDefinition['group'], Achievement[]>();

  for (const achievement of achievements) {
    groups.set(achievement.group, [...(groups.get(achievement.group) ?? []), achievement]);
  }

  return [...groups].map(([group, entries]) => ({
    group,
    entries,
    unlocked: entries.filter((entry) => entry.unlocked).length,
  }));
};

/**
 * The handful a summary panel has room for: what has been earned, in the order the full list shows
 * it, with the last slot kept for whatever is closest to unlocking. A profile that has earned
 * nothing yet fills every slot with what it is closest to instead, so the panel is never empty.
 */
export const summariseAchievements = (achievements: Achievement[], slots: number): Achievement[] => {
  const unlocked = achievements.filter((achievement) => achievement.unlocked);
  const locked = achievements
    .filter((achievement) => !achievement.unlocked)
    .sort((a, b) => b.progress / b.target - a.progress / a.target);
  const earned = unlocked.slice(0, locked.length === 0 ? slots : slots - 1);

  // Closest first, but one group at a time before a second from any of them. A profile that has
  // earned nothing is equally far from all 36, and closeness alone then falls back to declaration
  // order - six ways of counting the same songs, where one from each group gives eight different
  // things to aim at.
  const groups = new Set<AchievementDefinition['group']>();
  const firstOfEachGroup: Achievement[] = [];
  const rest: Achievement[] = [];

  for (const achievement of locked) {
    if (groups.has(achievement.group)) {
      rest.push(achievement);
    } else {
      groups.add(achievement.group);
      firstOfEachGroup.push(achievement);
    }
  }

  return [...earned, ...[...firstOfEachGroup, ...rest].slice(0, slots - earned.length)];
};

export const evaluateAchievements = (stats: ProfileStats, songs: SongLookup = {}): Achievement[] => {
  const context: Context = { stats, songs };

  return ACHIEVEMENTS.map(({ measure, ...definition }) => {
    const progress = Math.min(measure(context), definition.target);

    return { ...definition, progress, unlocked: progress >= definition.target };
  });
};

/**
 * Which achievements the most recent play was the one to unlock - by evaluating with and without
 * it. Nothing about an unlock is stored: it is a property of the plays, so it cannot drift.
 */
export const achievementsUnlockedByLatestPlay = (stats: ProfileStats, songs: SongLookup = {}): Achievement[] => {
  if (stats.plays.length === 0) return [];

  const newest = stats.plays.reduce((latest, play) => (play.date > latest.date ? play : latest));
  const before = summarisePlays(stats.plays.filter((play) => play !== newest));

  const alreadyUnlocked = new Set(
    evaluateAchievements(before, songs)
      .filter((achievement) => achievement.unlocked)
      .map((achievement) => achievement.id),
  );

  return evaluateAchievements(stats, songs).filter(
    (achievement) => achievement.unlocked && !alreadyUnlocked.has(achievement.id),
  );
};
